import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Filter, Search, CheckCircle, Clock, Loader2, CreditCard, Building2, Users, Calendar, TrendingUp, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => (n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const tipoLabel = { enganche: 'Con enganche', pago_unico: 'Pago único', pagos: 'En pagos' };
const estadoBadge = {
  pagada: 'bg-emerald-500/20 text-emerald-400',
  parcial: 'bg-amber-500/20 text-amber-400',
  pendiente: 'bg-zinc-700 text-zinc-300'
};

export default function Comisiones() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ comisiones: [], total_generado: 0, total_pagado: 0, total_pendiente: 0, count: 0 });
  const [comisionistas, setComisionistas] = useState([]);
  const [fraccionamientos, setFraccionamientos] = useState([]);

  // Filters
  const [filtroComisionista, setFiltroComisionista] = useState('todos');
  const [filtroFracc, setFiltroFracc] = useState('todos');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroDesde, setFiltroDesde] = useState('');
  const [filtroHasta, setFiltroHasta] = useState('');

  // Pago dialog
  const [pagoDialog, setPagoDialog] = useState({ open: false, comision: null });
  const [pagoForm, setPagoForm] = useState({ monto: '', metodo: 'transferencia', referencia: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const authRes = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (!authRes.ok) { navigate('/login'); return; }
        setUser(await authRes.json());
        const [comRes, fraccsRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/comisionistas`, { credentials: 'include' }),
          fetch(`${BACKEND_URL}/api/fraccionamientos`, { credentials: 'include' })
        ]);
        if (comRes.ok) setComisionistas(await comRes.json());
        if (fraccsRes.ok) setFraccionamientos(await fraccsRes.json());
      } catch { navigate('/login'); }
    };
    init();
  }, [navigate]);

  const loadComisiones = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroComisionista !== 'todos') params.set('comisionista_id', filtroComisionista);
      if (filtroFracc !== 'todos') params.set('fraccionamiento_id', filtroFracc);
      if (filtroEstado !== 'todos') params.set('estado', filtroEstado);
      if (filtroDesde) params.set('fecha_desde', filtroDesde);
      if (filtroHasta) params.set('fecha_hasta', filtroHasta);
      const res = await fetch(`${BACKEND_URL}/api/comisiones/lista?${params}`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filtroComisionista, filtroFracc, filtroEstado, filtroDesde, filtroHasta]);

  useEffect(() => { if (user) loadComisiones(); }, [user, loadComisiones]);

  const handlePago = async () => {
    if (!pagoDialog.comision || !pagoForm.monto) { toast.error('Ingresa un monto'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/comisiones/${pagoDialog.comision.comision_id}/pagar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ monto: parseFloat(pagoForm.monto), metodo: pagoForm.metodo, referencia: pagoForm.referencia || null })
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success('Pago registrado');
        setPagoDialog({ open: false, comision: null });
        setPagoForm({ monto: '', metodo: 'transferencia', referencia: '' });
        await loadComisiones();
      } else { toast.error(d.detail || 'Error'); }
    } catch { toast.error('Error de conexión'); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="comisiones-page" className="p-4 sm:p-6 space-y-5 max-w-[1200px] mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <DollarSign className="w-6 h-6 text-[#C6A54A]" />
          Comisiones
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Control de todas las comisiones generadas por ventas</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
          <div className="flex items-center gap-2 mb-1"><DollarSign className="w-3.5 h-3.5 text-[#C6A54A]" /><span className="text-xs text-zinc-500 uppercase">Generadas</span></div>
          <p data-testid="total-generado" className="text-xl font-bold text-[#C6A54A]">${fmt(data.total_generado)}</p>
        </div>
        <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
          <div className="flex items-center gap-2 mb-1"><CheckCircle className="w-3.5 h-3.5 text-emerald-400" /><span className="text-xs text-zinc-500 uppercase">Pagadas</span></div>
          <p data-testid="total-pagado" className="text-xl font-bold text-emerald-400">${fmt(data.total_pagado)}</p>
        </div>
        <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
          <div className="flex items-center gap-2 mb-1"><Clock className="w-3.5 h-3.5 text-amber-400" /><span className="text-xs text-zinc-500 uppercase">Pendientes</span></div>
          <p data-testid="total-pendiente" className="text-xl font-bold text-amber-400">${fmt(data.total_pendiente)}</p>
        </div>
        <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
          <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-3.5 h-3.5 text-blue-400" /><span className="text-xs text-zinc-500 uppercase">Registros</span></div>
          <p className="text-xl font-bold text-blue-400">{data.count}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-zinc-900/50 rounded-xl p-4 border border-zinc-800/50 space-y-3">
        <div className="flex items-center gap-2 text-sm text-zinc-400 font-medium">
          <Filter className="w-4 h-4" /> Filtros
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Select value={filtroComisionista} onValueChange={setFiltroComisionista}>
            <SelectTrigger data-testid="filter-comisionista" className="bg-zinc-800 border-zinc-700 text-white h-9 text-sm">
              <SelectValue placeholder="Comisionista" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="todos" className="text-white">Todos</SelectItem>
              {comisionistas.map(c => (
                <SelectItem key={c.user_id} value={c.user_id} className="text-white">{c.user_nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filtroFracc} onValueChange={setFiltroFracc}>
            <SelectTrigger data-testid="filter-fraccionamiento" className="bg-zinc-800 border-zinc-700 text-white h-9 text-sm">
              <SelectValue placeholder="Fraccionamiento" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="todos" className="text-white">Todos</SelectItem>
              {fraccionamientos.map(f => (
                <SelectItem key={f.fraccionamiento_id} value={f.fraccionamiento_id} className="text-white">{f.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filtroEstado} onValueChange={setFiltroEstado}>
            <SelectTrigger data-testid="filter-estado" className="bg-zinc-800 border-zinc-700 text-white h-9 text-sm">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="todos" className="text-white">Todos</SelectItem>
              <SelectItem value="pendiente" className="text-white">Pendiente</SelectItem>
              <SelectItem value="parcial" className="text-white">Parcial</SelectItem>
              <SelectItem value="pagada" className="text-white">Pagada</SelectItem>
            </SelectContent>
          </Select>
          <Input data-testid="filter-desde" type="date" value={filtroDesde} onChange={e => setFiltroDesde(e.target.value)}
            className="bg-zinc-800 border-zinc-700 text-white h-9 text-sm" placeholder="Desde" />
          <Input data-testid="filter-hasta" type="date" value={filtroHasta} onChange={e => setFiltroHasta(e.target.value)}
            className="bg-zinc-800 border-zinc-700 text-white h-9 text-sm" placeholder="Hasta" />
        </div>
      </div>

      {/* Comisiones list */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#C6A54A] animate-spin" /></div>
      ) : data.comisiones.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center">
          <DollarSign className="w-14 h-14 text-zinc-700 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Sin comisiones</h3>
          <p className="text-sm text-zinc-500">Las comisiones se generan al vender lotes con comisionista asignado</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.comisiones.map(c => (
            <div key={c.comision_id} data-testid={`comision-row-${c.comision_id}`}
              className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-white font-semibold text-sm">Lote #{c.lote_numero}</span>
                    <span className="text-zinc-500 text-xs">Mz {c.lote_manzana}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${estadoBadge[c.estado] || estadoBadge.pendiente}`}>
                      {c.estado === 'pagada' ? 'Pagada' : c.estado === 'parcial' ? 'Parcial' : 'Pendiente'}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-zinc-800 text-zinc-400">
                      {tipoLabel[c.tipo_liquidacion] || 'Pago único'}
                      {c.tipo_liquidacion === 'pagos' && c.numero_pagos ? ` (${c.numero_pagos})` : ''}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-400">
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" />{c.comisionista_nombre}</span>
                    <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{c.fraccionamiento_nombre}</span>
                    <span>Cliente: {c.cliente_nombre || '-'}</span>
                    {c.fecha_venta && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(c.fecha_venta).toLocaleDateString('es-MX')}</span>}
                  </div>
                  {/* Financial details */}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs">
                    <span className="text-zinc-500">Venta: ${fmt(c.precio_venta)}</span>
                    <span className="text-[#C6A54A]">{c.porcentaje_aplicado}% → ${fmt(c.monto_total)}</span>
                    {c.monto_pagado > 0 && <span className="text-emerald-400">Pagado: ${fmt(c.monto_pagado)}</span>}
                    {c.saldo_pendiente > 0 && <span className="text-amber-400 font-medium">Pendiente: ${fmt(c.saldo_pendiente)}</span>}
                  </div>
                  {/* Scheduled payments inline */}
                  {c.pagos_programados?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {c.pagos_programados.map(pp => (
                        <span key={pp.pago_programado_id}
                          className={`px-2 py-0.5 rounded text-[10px] ${pp.estado === 'pagado' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-800 text-zinc-400'}`}>
                          #{pp.numero_pago} ${fmt(pp.monto)} {pp.estado === 'pagado' ? '✓' : new Date(pp.fecha_programada).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Actual payments */}
                  {c.pagos?.length > 0 && (
                    <div className="mt-2 space-y-0.5">
                      {c.pagos.map((p, i) => (
                        <div key={i} className="text-xs text-zinc-500">
                          <span className="text-emerald-400">${fmt(p.monto)}</span> · {p.metodo} · {new Date(p.fecha).toLocaleDateString('es-MX')}
                          {p.referencia && <span className="text-zinc-600"> · {p.referencia}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="shrink-0 flex flex-col items-end gap-2">
                  <div className="text-right">
                    <p className="text-lg font-bold text-[#C6A54A]">${fmt(c.monto_total)}</p>
                    <p className="text-xs text-zinc-500">{c.porcentaje_aplicado}%</p>
                  </div>
                  {c.saldo_pendiente > 0 && (
                    <Button data-testid={`pagar-${c.comision_id}`} size="sm"
                      onClick={() => { setPagoDialog({ open: true, comision: c }); setPagoForm({ monto: c.saldo_pendiente.toString(), metodo: 'transferencia', referencia: '' }); }}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7">
                      <CreditCard className="w-3 h-3 mr-1" /> Pagar
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pago Dialog */}
      <Dialog open={pagoDialog.open} onOpenChange={open => !open && setPagoDialog({ open: false, comision: null })}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-emerald-400" /> Registrar Pago</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {pagoDialog.comision && `Lote #${pagoDialog.comision.lote_numero} · Saldo: $${fmt(pagoDialog.comision.saldo_pendiente)}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300 text-sm">Monto *</Label>
              <Input data-testid="pago-monto" type="number" step="0.01" min="0"
                value={pagoForm.monto} onChange={e => setPagoForm(p => ({...p, monto: e.target.value}))}
                className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Método</Label>
              <Select value={pagoForm.metodo} onValueChange={v => setPagoForm(p => ({...p, metodo: v}))}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="transferencia" className="text-white">Transferencia</SelectItem>
                  <SelectItem value="efectivo" className="text-white">Efectivo</SelectItem>
                  <SelectItem value="cheque" className="text-white">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Referencia (opcional)</Label>
              <Input data-testid="pago-referencia" value={pagoForm.referencia}
                onChange={e => setPagoForm(p => ({...p, referencia: e.target.value}))}
                className="bg-zinc-800 border-zinc-700 text-white" placeholder="No. de operación" />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setPagoDialog({ open: false, comision: null })} className="text-zinc-400">Cancelar</Button>
              <Button data-testid="confirm-pago-btn" onClick={handlePago} disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />} Pagar
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
